import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import CloseIcon from '@/components/Icons/CloseIcon';
import { EntityType } from '@/domain/entities';
import type { File } from '@/domain/files';
import { useCallback, type FC } from 'react';

interface Props {
  images: File[];
  selectedImageIndex: number | undefined;
  setSelectedImageIndex: (id: number | undefined) => void;
}

const Gallery: FC<Props> = ({ images, selectedImageIndex, setSelectedImageIndex }) => {
  const onNext = useCallback(() => {
    if (selectedImageIndex === undefined) {
      return;
    }

    setSelectedImageIndex(selectedImageIndex === images.length - 1 ? 0 : selectedImageIndex + 1);
  }, [selectedImageIndex, images, setSelectedImageIndex]);

  const onPrev = useCallback(() => {
    if (selectedImageIndex === undefined) {
      return;
    }

    setSelectedImageIndex(selectedImageIndex === 0 ? images.length - 1 : selectedImageIndex - 1);
  }, [selectedImageIndex, images, setSelectedImageIndex]);

  if (selectedImageIndex === undefined) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={() => setSelectedImageIndex(undefined)}
    >
      <button
        onClick={() => setSelectedImageIndex(undefined)}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
        aria-label="Close lightbox"
      >
        <CloseIcon />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-4 text-white hover:text-gray-300 transition-colors z-50"
        aria-label="Previous image"
      >
        <ChevronLeftIcon />
      </button>

      <div
        className="relative max-w-7xl max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[selectedImageIndex].url}
          alt={images[selectedImageIndex].description}
          className="max-w-full max-h-[75vh] object-contain rounded-none"
        />
        <div className="mt-4 text-center">
          <h3 className="text-white text-xl font-semibold">
            {images[selectedImageIndex].description}
          </h3>
          <p className="text-gray-300 capitalize mt-1">
            {
              images[selectedImageIndex].fileEntities?.find(
                (entity) => entity.entityType === EntityType.COMMUNITY,
              )?.metadata?.category
            }
          </p>
        </div>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-4 text-white hover:text-gray-300 transition-colors z-50"
        aria-label="Next image"
      >
        <ChevronRightIcon />
      </button>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
        {selectedImageIndex + 1} / {images.length}
      </div>
    </div>
  );
};

export default Gallery;
